import * as yup from 'yup';
import { FormManager, useForm } from 'react-form-state-manager';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import keyBy from 'lodash/keyBy';

import {
  EditOrderInput,
  EditUpgradeInput,
  GetOrderQuery as GetOrder,
} from '__generated__/graphql';

import { ErrorBag, filterPrefix, getServerErrors } from '../../../../common/helpers';
import {
  convertToParticipantFieldEntryInput,
  getDisabledChoices,
  getParticipantFieldEntrySchema,
  prefillFieldEntries,
} from '../../../../common/ParticipantFields/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import EditOrderFromBackendMutation from './EditOrderFromBackendMutation';
import ParticipantFieldInput from '../../../../common/ParticipantFields/ParticipantFieldInput';
import UI from '../../../../common/UI';
import useMutation from '../../../../api/useMutation';
import useScroll from '../../../../common/useScroll';
import useValidation from '../../../../common/useValidation';

type Order = GetOrder['order'];
type ParticipantField = Order['participant_fields'][0];
type Upgrade = Order['stand_alone_upgrades'][0];

const getValues = (order: Order) => ({
  id: order.id,
  fields: prefillFieldEntries(
    order.participant_field_entries.map(convertToParticipantFieldEntryInput),
    order.participant_fields,
  ),
  stand_alone_upgrades: order.stand_alone_upgrades
    .filter((upgrade) => upgrade.participant_fields.length > 0)
    .map((upgrade) => ({
      id: upgrade.id,
      fields: prefillFieldEntries(
        upgrade.participant_field_entries.map(convertToParticipantFieldEntryInput),
        upgrade.participant_fields,
      ),
    })),
});

const getEditOrderSchema = (participantFields: ParticipantField[]) => (
  yup.object().shape({
    fields: yup.array(getParticipantFieldEntrySchema(participantFields)),
  })
);

interface UpgradeFormProps {
  form: FormManager<EditOrderInput>;
  upgrade: Upgrade;
  formKey: string;
  errors?: ErrorBag;
}

const UpgradeForm = ({ form, upgrade, formKey, errors }: UpgradeFormProps) => {
  const formValue: EditUpgradeInput = form.get(formKey);
  const keyedFields = keyBy(upgrade.participant_fields, 'id');

  /** Field entries that are already present on the upgrade */
  const existingFieldEntries = keyBy(upgrade.participant_field_entries || [], 'id');

  return (
    <>
      <UI.Legend>
        <UI.Delimit>
          {upgrade.product.title}
          {upgrade.promotion.title}
          {upgrade.product_variant?.title}
        </UI.Delimit>
      </UI.Legend>
      {formValue.fields.map((value, fieldIndex) => (
        <ParticipantFieldInput
          value={value}
          onChange={(newValue) => form.set(`${formKey}.fields.${fieldIndex}`, {
            ...formValue.fields[fieldIndex],
            ...newValue,
          })}
          onBlur={() => form.setTouched(`${formKey}.fields.${fieldIndex}`, true)}
          touched={form.getTouched(`${formKey}.fields.${fieldIndex}`)}
          field={keyedFields[value.participant_field.id]}
          disabledChoices={existingFieldEntries[upgrade.id] ? getDisabledChoices(
            keyedFields[value.participant_field.id],
            existingFieldEntries[upgrade.id],
          ) : []}
          errors={filterPrefix(errors, `${formKey}.fields.${fieldIndex}`)}
          key={value.participant_field.id}
        />
      ))}
    </>
  );
};

export interface EditExtraInfoFormProps {
  order: Order;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const EditExtraInfoForm = ({ order, onSuccess, onCancel }: EditExtraInfoFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const { scrollToIfInvisible } = useScroll();

  // Make all fields optional in the interface and validation.
  const fields = order.participant_fields.map((field) => ({
    ...field,
    required: false,
  }));
  const standAloneUpgrades = order.stand_alone_upgrades.map((upgrade) => ({
    ...upgrade,
    participant_fields: upgrade.participant_fields.map((field) => ({
      ...field,
      required: false,
    })),
  }));

  const keyedFields = keyBy([
    ...fields,
    ...standAloneUpgrades.reduce(
      (fields, upgrade) => [...fields, ...upgrade.participant_fields],
      [] as ParticipantField[],
    ),
  ], 'id');

  const form = useForm<EditOrderInput>({
    values: getValues({
      ...order,
      participant_fields: fields,
      stand_alone_upgrades: standAloneUpgrades,
    }),
  });

  const [editOrder, { error, loading }] = useMutation(
    EditOrderFromBackendMutation,
    {
      variables: {
        input: form.values,
      },
      onCompleted: () => {
        notifier.success(t('notifications.order_edited'));
        onSuccess?.();
      },
      onError: () => {
        scrollToIfInvisible('EditExtraInfoForm');
      },
    },
  );

  const editOrderSchema = useMemo(() => getEditOrderSchema(fields), [fields]);

  const { valid, errors } = useValidation({
    schema: editOrderSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  // Always enable submit when the form contains new field entries.
  const containsNewFieldEntry = form.values.fields.filter((fieldEntry) => !fieldEntry.id).length > 0;

  return (
    <UI.Form onSubmit={() => editOrder()} id="EditExtraInfoForm">
      <UI.FormGrid>
        <UI.Legend>
          {t('edit_extra_information')}
        </UI.Legend>
        <UI.ServerErrors error={error} />

        {form.values.fields.map((value, index) => (
          <ParticipantFieldInput
            value={value}
            onChange={(newValue) => form.set(`fields.${index}`, { ...form.values.fields[index], ...newValue })}
            onBlur={() => form.setTouched(`fields.${index}`, true)}
            touched={form.getTouched(`fields.${index}`)}
            field={keyedFields[value.participant_field.id]}
            errors={filterPrefix(errors, `fields.${index}`)}
            key={value.participant_field.id}
          />
        ))}

        {form.values.stand_alone_upgrades.map((value, index) => (
          <Fragment key={value.id}>
            <UI.HR />
            <UpgradeForm
              form={form}
              upgrade={standAloneUpgrades.find(({ id }) => id === value.id)}
              formKey={`stand_alone_upgrades.${index}`}
              errors={errors}
            />
          </Fragment>
        ))}

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save')}
          loading={loading}
          valid={valid}
          onClose={onCancel}
          enabled={form.changed() || containsNewFieldEntry}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditExtraInfoForm;
